import { useEffect, useState } from "react";
import { ArrowRight, ChevronDown, Play, Plus, Send, X } from "lucide-react";
import { trackApp } from "@/components/app/appData";
import "./faq.css";

const A = "/design/";

const SUPPORT_URL = "https://t.me/episnyasupport?text="
  + encodeURIComponent("Привіт! Не знайшов(-ла) відповіді в FAQ: ");

function FaqIcon({ name, size = 20 }) {
  return <img src={`${A}icons-lg/${name}.svg`} alt="" style={{ width: size, height: size }} />;
}

function VideoModal({ video, title, onClose }) {
  useEffect(() => {
    if (!video) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [video, onClose]);

  if (!video) return null;

  return (
    <div className="faq-vmodal" role="dialog" aria-modal="true" aria-label={title}>
      <div className="faq-vmodal-back" onClick={onClose} />
      <div className="faq-vmodal-card">
        <div className="faq-vmodal-stage">
          <div className="faq-vmodal-video-wrap">
            <video className="faq-vmodal-video" controls playsInline preload="metadata"
              poster={video.poster} src={video.src} />
            <button type="button" className="faq-vmodal-close" onClick={onClose} aria-label="Закрити">
              <X size={18} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function FaqPage({ onOpenExamples, onOpenPricing }) {
  const [openId, setOpenId] = useState("create-song");
  const [videoOpen, setVideoOpen] = useState(null);
  const [tipsOpen, setTipsOpen] = useState(false);

  const toggle = (id) => {
    setVideoOpen(null);
    setOpenId((cur) => (cur === id ? null : id));
  };

  const items = [
    {
      id: "create-song",
      icon: "music",
      q: "Як створити пісню?",
      video: {
        title: "Як створити пісню",
        src: `${A}video/create-song-guide.mp4`,
        poster: `${A}video/create-song-guide.jpg`,
      },
      a: (
        <>
          <p>У відео — повний шлях: оберіть привід, жанр, настрій, мову та голос, розкажіть історію або вставте готовий текст.</p>
          <p>Після оплати пісня з’явиться у розділі «Мої пісні» та прийде в Telegram-бот.</p>
        </>
      ),
    },


    {
      id: "lyrics-preview",
      icon: "document",
      q: "Як прочитати слова майбутньої пісні до створення?",
      video: {
        title: "Як прочитати слова пісні",
        src: `${A}video/lyrics-preview-guide.mp4`,
        poster: `${A}video/lyrics-preview-guide.jpg`,
      },
      a: (
        <>
          <p>Система створює текст автоматично після оплати — на основі вашого опису.</p>
          <p>Якщо хочете побачити слова до створення пісні, спочатку поповніть баланс генерацій: тоді на кроці з описом з’явиться генерація тексту. Детальніше — у відео.</p>
        </>
      ),
    },


    {
      id: "upload-lyrics",
      icon: "upload-2",
      q: "Як завантажити свої слова?",
      a: (
        <>
          <p>На кроці опису історії (крок 6) увімкніть перемикач «У мене є готовий текст» і вставте слова у поле.</p>
          <p>Важливо: система проспіває дослівно те, що ви напишете — без автоматичної переробки тексту.</p>
        </>
      ),
    },


    {
      id: "create-video",
      icon: "youtube-play",
      q: "Як створити відео з музикою?",
      video: {
        title: "Як створити відео з музикою",
        src: `${A}video/create-video-guide.mp4`,
        poster: `${A}video/create-video-guide.jpg`,
      },
      a: (
        <>
          <p>Відкрийте «Музика на фото» на головній. Оберіть свою пісню або завантажте будь-яку музику, додайте від 1 до 30 фото, формат 9:16, 1:1 чи 16:9 і перехід між кадрами.</p>
          <p>Відео з вашою піснею — безкоштовно в межах денного ліміту. Відео з будь-якою музикою — 99 ₴.</p>
        </>
      ),
    },


    {
      id: "how-it-works",
      icon: "idea",
      q: "Як це працює?",
      a: (
        <>
          <p>Ви обираєте привід, стиль, настрій, мову та голос. Далі розказуєте історію своїми словами або вставляєте готовий текст.</p>
          <p>ШІ сам згенерує текст на основі вашого опису. Після перевірки оплати студія створює пісню з вокалом, а ви отримуєте два варіанти у «Моїх піснях» і в боті.</p>
        </>
      ),
    },


    {
      id: "creation-time",
      icon: "clock",
      q: "Скільки часу займає створення пісні?",
      a: (
        <>
          <p>Зазвичай 3–5 хвилин після підтвердження оплати.</p>
          <p>У години пік або при великому навантаженні може знадобитися до 20 хвилин.</p>
        </>
      ),
    },


    {
      id: "download-share",
      icon: "download",
      q: "Як переслати й завантажити пісню?",
      a: (
        <>
          <p>У «Моїх піснях» відкрийте пісню: там можна прослухати трек, завантажити MP3 і поділитися посиланням на публічну сторінку.</p>
          <p>Кнопка «Надіслати в бот» надішле обидва варіанти та посилання прямо в чат Telegram — звідти файл можна переслати або зберегти.</p>
        </>
      ),
    },


    {
      id: "genre",
      icon: "star",
      q: "Який обрати жанр?",
      a: (
        <>
          <p>Прослухайте приклади звучання — так простіше відчути різницю між стилями. Якщо вагаєтесь, почніть з «Поп» або «Мікс жанрів».</p>
          <p>Для незвичного звучання оберіть «Свій стиль» і опишіть бажаний настрій або виконавця.</p>
          <button type="button" className="faq-inline-btn" onClick={onOpenExamples}>
            Послухати приклади <ArrowRight size={15} />
          </button>
        </>
      ),
    },


    {
      id: "replace-lyrics",
      icon: "refresh",
      q: "Чи можна змінити слова пісні?",
      a: (
        <>
          <p>Так. Якщо під час вибору тарифу увімкнути «Перегенерацію» (+30 ₴), після створення пісні можна відредагувати слова й переспівати трек у тому самому стилі. Одна перегенерація безкоштовна.</p>
          <p>Також можна переглянути слова до створення: спочатку поповніть баланс генерацій, згенеруйте текст на кроці опису та відредагуйте його перед оплатою пісні.</p>
          <button type="button" className="faq-balance-btn" onClick={onOpenPricing}>
            <Plus size={15} /> Поповнити баланс
          </button>
        </>
      ),
    },


    {
      id: "song-length",
      icon: "appointment-reminders",
      q: "Скільки триває пісня?",
      a: (
        <>
          <p>Приблизно 3–4 хвилини. Тривалість залежить від жанру та вашого опису, а не від кількості рядків. Навіть короткий опис зазвичай дає пісню близько 3 хвилин.</p>
          <p>На кожну пісню ви отримуєте два варіанти виконання.</p>
        </>
      ),
    },
  ];

  return (
    <div className="faq-page" data-testid="faq-page">
      <section className="faq-hero glass-surface" aria-labelledby="faq-title">
        <div className="faq-hero-top">
          <span className="faq-hero-icon"><FaqIcon name="speech-bubble" size={30} /></span>
          <div className="faq-hero-txt">
            <span className="faq-kicker">Довідка E-PISNYA</span>
            <h2 id="faq-title">Часті питання</h2>
          </div>
        </div>
        <p className="faq-hero-desc">
          Тут зібрані відповіді про створення пісень і відео. Якщо чогось не знайшли — підтримка поруч.
        </p>
        <div className="faq-hero-support">
          <span>Не знайшли відповіді?</span>
          <a className="faq-support-compact" href={SUPPORT_URL} target="_blank" rel="noopener noreferrer"
            data-testid="faq-support" onClick={() => trackApp("faq: клік підтримки", "")}>
            <Send size={14} /> Написати
          </a>
        </div>
      </section>

      <div className="faq-list">
        {items.map((item) => {
          const isOpen = openId === item.id;
          const panelId = `faq-panel-${item.id}`;
          return (
            <article key={item.id} className={"faq-item" + (isOpen ? " open" : "")}>
              <button type="button" className="faq-q" aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={(e) => {
                  e.currentTarget.blur();
                  trackApp(`faq: ${isOpen ? "закрив" : "відкрив"} «${item.q}»`, "");
                  toggle(item.id);
                }}>
                <span className="faq-q-icon"><FaqIcon name={item.icon} size={19} /></span>
                <span className="faq-q-text">{item.q}</span>
                <span className="faq-chevron" aria-hidden="true"><ChevronDown size={17} /></span>
              </button>
              <div className="faq-a" id={panelId} role="region" inert={isOpen ? undefined : true}>
                <div className="faq-a-inner">
                  <div className="faq-a-body">
                    {isOpen && item.video ? (
                      <button type="button" className="faq-video-card"
                        aria-label={`Дивитися відео: ${item.q}`}
                        onClick={() => {
                          trackApp(`faq: відкрив відео «${item.q}»`, "");
                          setVideoOpen(item.video);
                        }}>
                        <span className="faq-video-poster">
                          <img src={item.video.poster} alt="" />
                          <span className="faq-video-play"><Play size={18} fill="currentColor" /></span>
                        </span>
                      </button>
                    ) : null}
                    {item.a}
                  </div>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      <section className={"faq-tips glass-surface" + (tipsOpen ? " open" : "")}>
        <button type="button" className="faq-tips-head" aria-expanded={tipsOpen}
          onClick={() => {
            setTipsOpen((v) => !v);
            trackApp(`faq: ${tipsOpen ? "закрив" : "відкрив"} рекомендації`, "");
          }}>
          <span className="faq-tips-icon"><FaqIcon name="idea-green" size={19} /></span>
          <span className="faq-tips-title">Рекомендації</span>
          <span className="faq-chevron" aria-hidden="true"><ChevronDown size={17} /></span>
        </button>
        <div className="faq-tips-wrap" inert={tipsOpen ? undefined : true}>
          <div className="faq-tips-inner">
            <ul className="faq-tips-body">
              <li>Для індивідуального тексту важливо зробити хороший опис: вкажіть імена та факти, які хочете почути в пісні. Інакше текст вийде шаблонним, а вам потрібен індивідуальний.</li>
              <li>Не суперечте жанру й настрою. Наприклад, «танцювальна + зворушлива» дасть зворушливий текст під танцювальний біт.</li>
              <li>Якщо вставляєте власні слова, не пишіть службові фрази на кшталт «слова пісні» — система проспіває їх дослівно.</li>
              <li>Не вгадуйте. Якщо щось незрозуміло — завжди напишіть у підтримку.</li>
            </ul>
          </div>
        </div>
      </section>

      <VideoModal video={videoOpen} title={videoOpen?.title || ""} onClose={() => setVideoOpen(null)} />
    </div>
  );
}
